export interface QuickCard {
  icon: string;
  title: string;
  desc: string;
  href: string;
}

export const quickCards: QuickCard[] = [
  {
    icon: "ri-map-pin-2-line",
    title: "最新地址",
    desc: "汇总当前可用的访问入口，按更新时间排列，帮你快速找到能打开的线路。",
    href: "#address",
  },
  {
    icon: "ri-global-line",
    title: "官方网址",
    desc: "说明如何辨别官方入口、核对页面特征，尽量避开仿冒站点与钓鱼页。",
    href: "#official",
  },
  {
    icon: "ri-download-2-line",
    title: "App 下载",
    desc: "分平台整理安装方式与注意事项，覆盖安卓、iOS 与桌面端。",
    href: "#app",
  },
  {
    icon: "ri-tools-line",
    title: "打不开怎么办",
    desc: "域名失效、加载卡顿、证书警告等常见问题的原因与逐步排查方法。",
    href: "#troubleshoot",
  },
];

export interface Feature {
  icon: string;
  title: string;
  desc: string;
}

export const overviewFeatures: Feature[] = [
  {
    icon: "ri-links-line",
    title: "地址一站式汇总",
    desc: "把分散在各处的访问入口集中整理，标注更新时间与线路状态，不用再到各种群聊和转载页里翻找。",
  },
  {
    icon: "ri-search-eye-line",
    title: "失效原因快速排查",
    desc: "针对「打不开」的不同表现给出对应原因和处理步骤，从网络、缓存到域名逐项排除，而不是反复刷新碰运气。",
  },
  {
    icon: "ri-shield-check-line",
    title: "官方入口真伪辨识",
    desc: "从域名写法、页面特征、更新记录等角度判断入口是否可靠，减少误入仿冒站、被弹窗和跳转牵着走的风险。",
  },
  {
    icon: "ri-route-line",
    title: "备用访问方案",
    desc: "准备多条备用线路与不同访问方式，主入口失效时也能维持基本访问，避免出现完全找不到入口的情况。",
  },
];

export interface Step {
  no: string;
  title: string;
  desc: string;
}

export const addressSteps: Step[] = [
  {
    no: "01",
    title: "确认当前地址状态",
    desc: "先对照公告里标注的更新时间，判断手上保存的地址是刚刚更新，还是已经过期需要替换。",
  },
  {
    no: "02",
    title: "从官方渠道获取",
    desc: "优先查看官方公告页、订阅通知或客服发布的信息，第三方转载和陌生私聊链接只作参考。",
  },
  {
    no: "03",
    title: "逐条测试线路",
    desc: "按顺序打开候选地址，记录哪些能正常加载、哪些提示失效或跳转到无关页面，心里有数再选择。",
  },
  {
    no: "04",
    title: "收藏并标注日期",
    desc: "把验证通过的入口加入收藏，并顺手标注获取时间，下次到期后可以快速定位并替换。",
  },
];

export interface Channel {
  icon: string;
  name: string;
  desc: string;
  note: string;
}

export const addressChannels: Channel[] = [
  {
    icon: "ri-notification-3-line",
    name: "官方公告页",
    desc: "通常是更新最快的地方，会说明更换原因、生效时间以及新旧地址的对应关系。",
    note: "更新最快",
  },
  {
    icon: "ri-mail-send-line",
    name: "邮件订阅通知",
    desc: "订阅后地址发生变动会通过邮件推送，适合不方便频繁手动查公告的用户。",
    note: "被动接收",
  },
  {
    icon: "ri-team-line",
    name: "客服与社群",
    desc: "通过官方客服或用户社群获取临时入口，注意先核实发布者身份，避免被引导到仿冒页。",
    note: "需核实身份",
  },
  {
    icon: "ri-compass-3-line",
    name: "地址导航页",
    desc: "把多个入口集中罗列并标注可用状态，适合一次性查看全部线路、快速做取舍。",
    note: "集中查看",
  },
];

export interface TroubleItem {
  q: string;
  cause: string;
  solutions: string[];
  level: string;
}

export const troubleItems: TroubleItem[] = [
  {
    q: "打开后提示域名不存在，或被跳转到广告页",
    cause: "域名已经失效，或者旧地址被重定向到了无关的宣传页面。",
    solutions: [
      "改用公告中最新公布的地址重新打开",
      "清除浏览器对旧地址的自动补全与历史记录",
      "确认没有误输入、复制多余字符或空格",
    ],
    level: "常见",
  },
  {
    q: "网页一直转圈，迟迟加载不出来",
    cause: "当前线路拥堵，或者本地网络对该地址的解析出现异常。",
    solutions: [
      "切换到另一条备用线路再次尝试",
      "更换网络环境，例如从 Wi-Fi 切换到移动数据",
      "稍等几分钟后重新加载，避开访问高峰",
    ],
    level: "常见",
  },
  {
    q: "浏览器提示「连接不安全」或出现证书警告",
    cause: "该入口与证书不匹配，可能并非可靠入口，存在被仿冒的可能。",
    solutions: [
      "先核对当前地址是否与公告中的写法一致",
      "不要在该页面输入任何账号、密码或支付信息",
      "改用经过核对无误的入口重新访问",
    ],
    level: "需注意",
  },
  {
    q: "电脑能打开，手机却始终打不开",
    cause: "移动端的 DNS 缓存或浏览器版本导致解析与兼容问题。",
    solutions: [
      "在手机浏览器中清空缓存后重试",
      "把浏览器更新到较新的版本",
      "换一个浏览器打开，排除单一内核的问题",
    ],
    level: "常见",
  },
  {
    q: "页面能打开，但内容错乱或一片空白",
    cause: "页面脚本没有加载完成，或者浏览器拦截了部分资源。",
    solutions: [
      "关闭广告拦截类插件后重新刷新",
      "强制刷新页面，忽略本地缓存的旧文件",
      "更换浏览器内核再访问一次",
    ],
    level: "少见",
  },
  {
    q: "频繁被要求验证，弹窗一个接一个",
    cause: "很可能进入了仿冒或夹带广告的页面，而非真正的官方入口。",
    solutions: [
      "立即关闭页面，并回到公告核对入口来源",
      "不要在弹窗内点击任何下载或授权按钮",
      "从官方公告重新获取地址后再访问",
    ],
    level: "需注意",
  },
];

export interface Check {
  icon: string;
  title: string;
  desc: string;
}

export const officialChecks: Check[] = [
  {
    icon: "ri-link",
    title: "核对地址栏写法",
    desc: "打开的域名应与公告中给出的写法完全一致，多一个字母、少一段后缀都要提高警惕。",
  },
  {
    icon: "ri-calendar-check-line",
    title: "对照更新记录",
    desc: "页面上标注的更新日期应与公告保持同步，长期停留在旧日期的入口需要重新确认。",
  },
  {
    icon: "ri-error-warning-line",
    title: "留意仿冒特征",
    desc: "一进页面就强制跳转、密集弹窗、诱导下载不明文件，通常说明这不是可靠的官方入口。",
  },
  {
    icon: "ri-stack-line",
    title: "多渠道交叉验证",
    desc: "把公告、订阅通知与客服信息放在一起对比，只有在多处一致的入口才更值得信任。",
  },
];

export interface PlatformGuide {
  id: string;
  label: string;
  icon: string;
  intro: string;
  steps: string[];
  notes: string[];
}

export const appPlatforms: PlatformGuide[] = [
  {
    id: "android",
    label: "安卓 Android",
    icon: "ri-android-fill",
    intro: "安卓安装相对灵活，但也正因为如此，来源不明的安装包风险更高，先确认渠道再动手。",
    steps: [
      "在官方公告页中找到安卓端的下载入口并打开",
      "下载完成后，在系统设置中允许本次未知来源安装",
      "安装结束后打开应用，按提示更新到最新版本",
    ],
    notes: ["不要安装来源不明的安装包", "安装前可核对文件大小与版本号是否与公告一致"],
  },
  {
    id: "ios",
    label: "iOS",
    icon: "ri-apple-fill",
    intro: "苹果设备的安装方式与安卓不同，通常需要借助浏览器的描述文件或直接使用网页版。",
    steps: [
      "使用 Safari 打开官方提供的安装链接",
      "按页面提示安装描述文件，并在设置中完成信任",
      "返回桌面确认图标出现后，再点击打开应用",
    ],
    notes: ["非 Safari 浏览器可能无法完成安装流程", "信任描述文件之前，请务必确认来源可靠"],
  },
  {
    id: "windows",
    label: "Windows",
    icon: "ri-windows-fill",
    intro: "桌面端可以优先使用网页版，只有需要长期使用时再考虑按照官方指引安装客户端。",
    steps: [
      "先用浏览器打开官方网页版，确认可以正常访问",
      "确实需要客户端时，从公告中的桌面端入口下载",
      "安装时留意附加勾选项，取消不需要的捆绑内容",
    ],
    notes: ["安装包建议统一保存在固定目录，方便日后替换与卸载"],
  },
  {
    id: "mac",
    label: "macOS",
    icon: "ri-macbook-line",
    intro: "macOS 对应用来源有安全限制，安装时可能需要在系统设置中手动放行才能正常运行。",
    steps: [
      "从官方入口下载对应系统版本的安装包",
      "在「系统设置 - 隐私与安全性」中允许该应用打开",
      "确认应用能够正常启动后，再继续后续操作",
    ],
    notes: ["若提示「无法验证开发者」，请先核对文件来源是否可靠"],
  },
];

export interface BackupRoute {
  icon: string;
  title: string;
  desc: string;
  suitable: string;
}

export const backupRoutes: BackupRoute[] = [
  {
    icon: "ri-swap-box-line",
    title: "备用域名",
    desc: "运营方通常会准备一到两个备用域名，主域名失效时可以优先切换到备用入口继续访问。",
    suitable: "主入口提示失效时",
  },
  {
    icon: "ri-speed-up-line",
    title: "加速线路",
    desc: "部分入口提供加速节点，适合在页面加载缓慢、频繁卡顿或缓冲时作为替代方案。",
    suitable: "页面加载缓慢时",
  },
  {
    icon: "ri-git-branch-line",
    title: "镜像入口",
    desc: "内容与主站基本一致的镜像地址，用于主站完全无法访问时快速恢复基本浏览。",
    suitable: "主站完全打不开时",
  },
  {
    icon: "ri-hard-drive-3-line",
    title: "离线缓存",
    desc: "对已经加载过的页面内容做本地缓存，在网络波动频繁时仍可查看部分已保存的信息。",
    suitable: "网络波动频繁时",
  },
];

export interface FaqItem {
  cat: string;
  q: string;
  a: string;
}

export const faqCategories: string[] = ["全部", "地址相关", "访问问题", "下载安装", "安全提示"];

export const faqItems: FaqItem[] = [
  {
    cat: "地址相关",
    q: "为什么地址会经常更换？",
    a: "访问入口可能因为域名到期、线路调整或外部原因而失效，运营方会不定期更换地址，所以任何一条地址都以最新公告为准，不要长期依赖单一条目。",
  },
  {
    cat: "地址相关",
    q: "以前保存的旧地址还能继续用吗？",
    a: "不一定。旧地址可能仍然可以打开，也可能被重定向到无关页面，建议每次使用前先核对更新时间，再决定是否继续使用。",
  },
  {
    cat: "地址相关",
    q: "在哪里能第一时间看到新地址？",
    a: "一般以官方公告页和订阅通知更新最快，客服与社群稍晚一些，第三方转载信息的时效性最差，仅适合用作参考。",
  },
  {
    cat: "访问问题",
    q: "同一个地址，别人能打开而我不行？",
    a: "访问结果会受本地网络、运营商解析和浏览器环境影响。可以更换网络、清理缓存后再试，必要时直接改用备用线路。",
  },
  {
    cat: "访问问题",
    q: "手机流量能打开、Wi-Fi 却打不开，怎么处理？",
    a: "这通常是 Wi-Fi 所属网络对该地址的解析出现异常，可以尝试调整 DNS 设置，或者暂时改用移动数据访问。",
  },
  {
    cat: "访问问题",
    q: "页面提示不安全，还能继续看吗？",
    a: "不建议。证书异常可能意味着入口不可靠，应先核对来源，确认无误后再访问，并且不要在可疑页面填写任何账号或支付信息。",
  },
  {
    cat: "下载安装",
    q: "App 应该从哪里下载？",
    a: "只从官方公告或官方页面中给出的入口下载。来源不明的安装包存在被改动、夹带多余程序的风险，安装前请先确认渠道。",
  },
  {
    cat: "下载安装",
    q: "安装时被系统拦截了怎么办？",
    a: "移动端需要手动允许本次安装，桌面端需要在安全设置中放行应用来源。放行之前，请务必确认文件来自可靠渠道。",
  },
  {
    cat: "下载安装",
    q: "一定要安装 App 才能访问吗？",
    a: "通常不是。多数情况下网页版即可完成基本访问，App 更适合需要长期、频繁使用的场景，两者并不是非此即彼的关系。",
  },
  {
    cat: "安全提示",
    q: "访问过程中需要注意哪些安全事项？",
    a: "不要在来源可疑的页面输入账号密码或支付信息，遇到强制下载、密集弹窗应立即关闭页面，并回到官方渠道重新获取入口。",
  },
  {
    cat: "安全提示",
    q: "收到自称官方的链接，可以直接点开吗？",
    a: "建议先与官方公告核对是否一致，只有经过多渠道确认的入口再打开，避免被仿冒页面一步步引导到风险页面。",
  },
];

export interface SafetyTip {
  icon: string;
  title: string;
  desc: string;
}

export const safetyTips: SafetyTip[] = [
  {
    icon: "ri-lock-password-line",
    title: "谨慎填写信息",
    desc: "在核对清楚的官方页面之外，不要输入账号、密码或任何支付相关信息，减少信息泄露的可能。",
  },
  {
    icon: "ri-file-warning-line",
    title: "警惕强制下载",
    desc: "页面自动弹出下载、要求安装插件时，先关闭页面，再重新核对入口来源，不要顺手点确认。",
  },
  {
    icon: "ri-history-line",
    title: "定期核对更新",
    desc: "每隔一段时间对照公告检查收藏的入口是否仍然有效，及时替换已经失效或过期的地址。",
  },
  {
    icon: "ri-save-3-line",
    title: "多处备份入口",
    desc: "把验证过的入口分别保存在浏览器收藏和便签中，避免单一记录丢失后无处可查。",
  },
];